import { Icon } from "@iconify/react";
import { Dialog } from "@mui/material";
import { GridApi } from "ag-grid-community";
import { formatDistance } from "date-fns";
import * as _ from "lodash";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useMatch, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  downloadGrantLetters,
  generateAcceptanceReport,
  generateCancellationReport,
  generateExerciseReport,
  generateGrantReport,
  generateTransactionReport,
  generateVestWiseReport,
  getGrantTemplate,
} from "../../api/Esop";
import SortingComponent from "../../components/SortingVariation";
import AlertDialog from "../../components/shared/AlertDialog";
import { ColumnOptions } from "../../components/shared/ColumnChooser";
import { ContextModal } from "../../components/shared/ContextModal";
import { Action, CTADropdown } from "../../components/shared/Dropdown";
import { EmptyTable } from "../../components/shared/EmptyTable";
import LazyPdfDocument from "../../components/shared/LazyPdfDocument";
import BasicMenu from "../../components/shared/Menu";
import Pagination from "../../components/shared/Pagination";
import TableDateFilters from "../../components/shared/TableDateFilters";
import TableRangeFilters from "../../components/shared/TableRangeFilters";
import {
  Box,
  ButtonPrimary,
  ButtonSize,
  Center,
  ExportTableData,
  HStack,
  TableDataExportProps,
  VStack,
  getCompanyName,
} from "../../components/utils";
import {
  useEmployeeAcceptReminder,
  useEmployees,
  queryClient,
} from "../../queries";
import { useEmployeePageDetails } from "../../queries/employees";
import {
  useAddGrantExcel,
  useDeleteGrant,
  useExportToExcel,
  useGenerateGrantLettersBulk,
  useGrants,
} from "../../queries/esopPlan";
import GenericTableHeader from "../../shared/GenericTableHeader";
import { useAuthorizationStore, useCompanyStore } from "../../store";
import { useError } from "../../store/errorStore";
import { useGrantFilterStore } from "../../store/grantsTableFilter";
import { usePermissionStore } from "../../store/permissionStore";
import { useBatchEventStore } from "../../store/batchEventStore";
import { useGetEmployeePortalSettings } from "../../queries/siteSettings";
import {
  useAccelerateVestingDialog,
  useExerciseRequestDialog,
  useGrantCreatedDialog,
} from "../../store/useDialogStore";
import { EmployementStatus } from "../../types/Employee";
import {
  grantHeadersObj,
  grantHeaderslist,
  grantHeadingVal,
} from "../../types/ExportExcelGrant";
import { Grant } from "../../types/Grant";
import { TriggerType } from "../../types/VestingTemplate";
import {
  convertBase64ToBlob,
  downloadBlobObject,
} from "../../utils/DownloadFile";
import { globalFilter, sort } from "../../utils/arrays";
import { determineUserAccessToResource } from "../../utils/auth";
import convertToBase64 from "../../utils/convertToBase64";
import {
  formatCurrency,
  getCurrencySymbol,
  getCurrencyType,
} from "../../utils/currencyFormatter";
import { formatDisplayDate } from "../../utils/date";
import useIsMobile from "../../utils/detectDevice";
import {
  Action as DefaultAction,
  Resource,
} from "../../utils/interfaces/Companies";
import { makeRangeData } from "../../utils/makeRangeDate";
import {
  _trimAll,
  getFormattedValue,
  limitString,
  stringFormat,
} from "../../utils/string";
import ExerciseReportDialog from "../allPlans/ExerciseReportDialog";
import { AddOrEditGrant } from "./AddOrEditGrant";
import AgGridGrantsTable from "./AgGridGrantsTable";
import Avatar from "./Avatar";
import AssignGrantLetterTemplatesModal from "./AssignGrantLetterTemplatesModal";
import BulkOptionStateChange from "./BulkOptionStateChange";
import EsopAccountingDialog from "./EsopAccountingDialog";
import FinancialReportDialog from "./FinancialReportDialog";
import SH6ReportDialog from "./SH6ReportDialog";
import GrantStatusLabel from "./StatusLabel";
import VestRequest from "./VestRequest";
import { ReportType } from "../reports/utils/report";
import { EsopPlan } from "../../types/EsopPlan";
import optionsApi from "../../api/optionsApi";
import GrantDocumentDialog from "./GrantDocumentDialog";
import { useGrantDetailsStore } from "../../store/useGrantDetailsStore";
import ExtendExerciseDialog from "./ExtendExerciseDialog";
import { useValuationDetails } from "../../queries/Valuation";

interface BulkActionsProps {
  actions: Action[];
  onAction: (action: Action) => void;
  title?: string;
}

function GrantsTable() {
  const currency = getCurrencyType();
  const currencySymbol = getCurrencySymbol();
  const companyName = getCompanyName();
  const { setState: setGrantCreatedDialog } = useGrantCreatedDialog();
  const { setState: setAccelerateVestingDialog } = useAccelerateVestingDialog();
  const [exportData, setExportData] = useState<TableDataExportProps>({});
  const grantsTableRef = useRef<GridApi | null>(null);
  const userPermission = useAuthorizationStore();
  const { permission } = usePermissionStore();
  const { grantsRefreshAt } = useBatchEventStore();

  let data: Grant[] = [];
  const rowData: Grant[] = [];

  const [uniqueByEmployeeDetails, setUniqueEmployeeDetails] = useState<number>(
    data.length
  );
  const [showSearch, setShowSearch] = useState<boolean>(false);
  const [grantsCount, setGrantsCount] = useState<number>(data.length);
  const [maskPiiData, setMaskPiiData] = useState<boolean>(false);

  useEffect(() => {
    handleTableFilterCount();
  }, [data]);

  const { id } = useParams();
  const navigate = useNavigate();

  const _id = id ?? "";

  const grantFilter = useGrantFilterStore();
  const isUserAdmin = useMemo(
    () =>
      determineUserAccessToResource(
        permission?.aclList || [],
        Resource.All,
        DefaultAction.All
      ),
    [permission]
  );

  const { data: employeePortalSettings } = useGetEmployeePortalSettings();
  const empDetailPath = useMatch({
    path: "/options/employeeDetails/:id",
    end: true,
    caseSensitive: true,
  });
  const isEmployeeDetailsView =
    !!empDetailPath?.pathname?.includes("employeeDetails");
  const { data: employeePageDetail } = useEmployeePageDetails(_id, {
    enabled: isEmployeeDetailsView && !!_id,
  });

  const planViewPath = useMatch({
    path: "/options/planView/:id",
    end: true,
    caseSensitive: true,
  });
  const grantsQueryParams = useMemo(() => {
    if (planViewPath) {
      return { pageSize: 1000, planId: _id };
    } else if (empDetailPath) {
      return { pageSize: 1000, employeeId: _id };
    }
    return { pageSize: 1000 };
  }, [planViewPath, empDetailPath, _id]);
  const {
    isPlaceholderData,
    isFetching,
    data: grants,
  } = useGrants(grantsQueryParams);
  const _grants = grants?.grants || [];
  // filter and sort logic
  const _data = useMemo(() => _grants || [], [isPlaceholderData, isFetching]);

  const isBatchRefetchRef = useRef(false);

  useEffect(() => {
    if (grantsRefreshAt > 0) {
      isBatchRefetchRef.current = true;
      queryClient.invalidateQueries("allGrants");
      queryClient.refetchQueries("allGrants");
    }
  }, [grantsRefreshAt]);

  useEffect(() => {
    if (!isFetching && isBatchRefetchRef.current && grantsTableRef.current) {
      grantsTableRef.current.refreshCells({ force: true });
      isBatchRefetchRef.current = false;
    }
  }, [isFetching]);
  const rangeData = makeRangeData(_data, _id);
  if (id !== "" && empDetailPath?.pathname.includes("employeeDetail")) {
    data = useMemo(
      () => _data.filter((grant) => grant.employeeId.toString() === _id),
      [isPlaceholderData, isFetching, id]
    );
  } else if (id !== "" && planViewPath?.pathname.includes("planView")) {
    data = useMemo(
      () => _data?.filter((grant) => grant?.planid?.toString() === _id),
      [isPlaceholderData, isFetching, id]
    );
  } else {
    data = _data;
  }
  const { mutate: deleteGrant, isLoading: deletingGrant } = useDeleteGrant();
  const { mutate: generateGrantLetters, isLoading: generatingGrant } =
    useGenerateGrantLettersBulk();
  const { data: employees } = useEmployees();
  const _employees = employees || [];
  const employeeData = _employees?.filter((item) => _id === item.id.toString());
  // pop up dialogs
  const [dialog, setDialog] = useState<{
    message?: string;
    open: boolean;
    grant?: Grant;
    mode?: "Edit" | "Add" | "Clone" | "Delete";
  }>({
    open: false,
    grant: undefined,
    mode: "Add",
    message: undefined,
  });
  const [reportDialog, setReportDialog] = useState<{
    open: boolean;
    type?: string;
  }>({
    open: false,
    type: undefined,
  });

  const [commonReportDialog, setCommonReportDialog] = useState<{
    open: boolean;
    action: Action;
    grants: Grant[];
  }>({
    open: false,
    action: { name: "", disabled: false, maskPiiData: false, type: "" },
    grants: [],
  });
  const grantStore = useGrantDetailsStore();
  const [documentDialog, setDocumentDialog] = useState<{
    open: boolean;
    grant?: Grant;
  }>({ open: false, grant: undefined });
  const [grantDocumentDialog, setGrantDocumentDialog] = useState<{
    open: boolean;
    grant?: Grant;
  }>({ open: false, grant: undefined });
  const [bulkActionDialog, setBulkActionDialog] = useState<{
    open: boolean;
    grantItem: Grant[];
    mode?:
      | "State Change"
      | "Delete"
      | "Generate"
      | "Send Reminder Emails"
      | "Assign Grant Templates";
    message?: string;
  }>({ open: false, grantItem: [], mode: "State Change", message: undefined });
  const [vestRequestDialog, setVestRequestDialog] = useState<{
    open: boolean;
    grant?: Grant;
  }>({ open: false, grant: undefined });
  const { state: exerciseRequestDialog, setState: setExerciseRequestDialog } =
    useExerciseRequestDialog();
  // filter and sort logic
  const [globalFilterText, setGlobalFilterText] = useState("");
  const [sortField, setSortField] = useState<{
    field: keyof Grant | "" | undefined;
    ascending: boolean;
  }>({ field: "optionHolderId", ascending: false });
  data = useMemo(() => {
    if (!data) return [];
    const filterResult = globalFilter(data, globalFilterText, [
      "optionHolderName",
      "optionHolderState",
      "planName",
      "vestingTemplateName",
      "grantIdentifier",
    ])
      .filter((gd) => {
        if (grantFilter.inColumnFilter.grantDate.length > 0) {
          for (const grantDate of grantFilter.inColumnFilter.grantDate) {
            if (
              new Date(grantDate).setHours(0, 0, 0, 0) ===
              new Date(gd.grantDate).setHours(0, 0, 0, 0)
            ) {
              return true;
            }
          }
          return false;
        }
        return gd;
      })
      .filter((og) => {
        if (grantFilter.inColumnFilter.optionsGranted.length > 0) {
          for (const range of grantFilter.inColumnFilter.optionsGranted) {
            const [min, max] = range.split(" - ").map(Number);
            if (og.optionsGranted >= min && og.optionsGranted <= max) {
              return true;
            }
          }
          return false;
        }
        return og;
      })
      .filter((ov) => {
        if (grantFilter.inColumnFilter.optionsVested.length > 0) {
          for (const range of grantFilter.inColumnFilter.optionsVested) {
            const [min, max] = range.split(" - ").map(Number);
            if (ov.optionsVested >= min && ov.optionsVested <= max) {
              return true;
            }
          }
          return false;
        }
        return ov;
      })

      .filter((og) => {
        if (grantFilter.inColumnFilter.optionsExercisedWithCash.length > 0) {
          for (const range of grantFilter.inColumnFilter
            .optionsExercisedWithCash) {
            const [min, max] = range.split(" - ").map(Number);
            if (
              og.optionsExercisedWithCash >= min &&
              og.optionsExercisedWithCash <= max
            ) {
              return true;
            }
          }
          return false;
        }
        return og;
      })
      .filter((ov) => {
        if (grantFilter.inColumnFilter.optionsExercisedWithStock.length > 0) {
          for (const range of grantFilter.inColumnFilter
            .optionsExercisedWithStock) {
            const [min, max] = range.split(" - ").map(Number);
            if (
              ov.optionsExercisedWithStock >= min &&
              ov.optionsExercisedWithStock <= max
            ) {
              return true;
            }
          }
          return false;
        }
        return ov;
      })
      .filter((ov) => {
        if (grantFilter.inColumnFilter.optionsExercised.length > 0) {
          for (const range of grantFilter.inColumnFilter.optionsExercised) {
            const [min, max] = range.split(" - ").map(Number);
            if (ov.optionsExercised >= min && ov.optionsExercised <= max) {
              return true;
            }
          }
          return false;
        }
        return ov;
      })
      .filter(
        (grant) =>
          !grantFilter.grantFilter[0].value ||
          !grantFilter.grantFilter[1].value ||
          (new Date(grant.grantDate) >=
            new Date(grantFilter.grantFilter[0].value) &&
            new Date(grant.grantDate) <=
              new Date(grantFilter.grantFilter[1].value))
      )
      .filter(
        (grant) =>
          !grantFilter.grantFilter[2].value ||
          _trimAll(grantFilter.grantFilter[2].value.toLowerCase()) ===
            _trimAll(grant.planName.toLowerCase())
      )
      .filter(
        (grant) =>
          !grantFilter.grantFilter[3].value ||
          _trimAll(grantFilter.grantFilter[3].value.toLowerCase()) ===
            _trimAll(grant.vestingTemplateName.toLowerCase())
      )
      .filter(
        (grant) =>
          !grantFilter.grantFilter[4].value ||
          _trimAll(grantFilter.grantFilter[4].value.toLowerCase()) ===
            _trimAll(stringFormat(grant.optionHolderState).toLowerCase())
      )
      .filter(
        (grant) =>
          !grantFilter.grantFilter[5].value ||
          parseInt(formatDistance(new Date(grant.grantDate), new Date()), 10) <=
            7
      )
      .filter(
        (grant) =>
          grant.optionsVested >= grantFilter.minOptionsVested &&
          grant.optionsVested <= grantFilter.maxOptionsVested
      )
      .filter(
        (grant) =>
          grant.optionsGranted >= grantFilter.minOptionsGranted &&
          grant.optionsGranted <= grantFilter.maxOptionsGranted
      );

    const sortResult = sort(
      filterResult,
      sortField?.field,
      sortField?.ascending
    );
    return sortResult;
  }, [data, globalFilterText, sortField, grantFilter.applyFilter, grantFilter]);
  useEffect(() => {
    setCurrentPage(1);
  }, [globalFilterText]);

  // selection logic
  const [selectedItems, setSelectedItems] = useState<Grant[]>([]);
  const onSelectionChangetemp2 = (item: Grant | Grant[]) => {
    const grantItem: Grant[] = [];
    if (_.isArray(item)) {
      const selectedGrantList = item.map((grant) => grant.grantIdentifier);
      const selectedGrant = data.filter((grant) =>
        selectedGrantList.includes(grant.grantIdentifier)
      );
      grantItem.push(...selectedGrant);
    } else {
      const selectedGrant = data.filter(
        (dataItem) => dataItem.grantIdentifier === item.grantIdentifier
      );
      grantItem.push(...selectedGrant);
    }
    setSelectedItems((items) => [...grantItem]);
  };
  const onSelectionChange = (item: Grant | Grant[]) => {
    const grantItem: Grant[] = [];

    if (_.isArray(item)) {
      const selectedGrantList = item.map((grant) => grant.grantIdentifier);

      const filteredRowIds: string[] = [];
      grantsTableRef.current?.forEachNodeAfterFilter((node: any) => {
        filteredRowIds.push(
          node?.data?.grantInfo?.props?.grant?.optionHolderId
        );
      });
      const filteredRows = data.filter((dataItem) =>
        filteredRowIds.includes(dataItem.optionHolderId)
      );
      const selectedGrants = filteredRows.filter((grant) =>
        selectedGrantList.includes(grant.grantIdentifier)
      );

      grantItem.push(...selectedGrants);
    } else {
      const selectedGrantIdentifier = item.grantIdentifier;

      const filteredRowIds: string[] = [];
      grantsTableRef.current?.forEachNodeAfterFilter((node: any) => {
        filteredRowIds.push(
          node?.data?.grantInfo?.props?.grant?.optionHolderId
        );
      });
      const filteredRows = data.filter((dataItem) =>
        filteredRowIds.includes(dataItem.optionHolderId)
      );

      const selectedGrant = filteredRows.find(
        (dataItem) => dataItem.grantIdentifier === selectedGrantIdentifier
      );

      if (selectedGrant) {
        grantItem.push(selectedGrant);
      }
    }

    setSelectedItems((items) => [...grantItem]);
  };
  const onSelectionChangetemp = (item: Grant) => {
    setSelectedItems((items) =>
      items.find((i) => i === item)
        ? items.filter((i) => i !== item)
        : [...items, item]
    );
  };
  const [selectAllChecked, setSelectAllChecked] = useState(false);
  useEffect(() => {
    if (selectAllChecked) {
      setSelectedItems(data);
    } else {
      setSelectedItems([]);
    }
  }, [data, selectAllChecked]);

  const errorMessage = useError();
  const [extendExerciseDialog, setExtendExerciseDialog] = useState<{
    open: boolean;
    grantIds: string[];
  }>({ open: false, grantIds: [] });

  //pagination logic
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const currentTableData = useMemo(() => {
    const firstPageIndex = (currentPage - 1) * pageSize;
    const lastPageIndex = firstPageIndex + pageSize;
    return data.slice(firstPageIndex, lastPageIndex);
  }, [data, currentPage, pageSize]);

  const fileRef = useRef() as React.MutableRefObject<HTMLInputElement>;

  let sumOptionsGranted = 0;
  let sumOptionsVested = 0;
  let sumOptionsUnvested = 0;
  let sumOptionsNetvested = 0;
  let sumOptionsStockExercised = 0;
  let sumOptionsCashedOut = 0;
  let sumOptionsExercised = 0;
  let sumOptionsLapsed = 0;
  let sumOptionsForfeited = 0;
  let sumTotal = 0;
  const isFractional = data?.some((grant) => grant.isFractional);

  data?.forEach((grant) => {
    sumOptionsGranted += grant.optionsGranted;
    sumOptionsVested += grant.optionsVested;
    sumOptionsUnvested += grant.optionsGranted - grant.optionsVested;
    sumOptionsNetvested +=
      grant.optionsVested -
      grant.optionsExercisedWithCash -
      grant.optionsExercisedWithStock -
      grant.optionsForfeited;
    sumOptionsStockExercised += grant.optionsExercisedWithStock;
    sumOptionsCashedOut += grant.optionsExercisedWithCash;
    sumOptionsExercised += grant.optionsExercised;
    sumOptionsLapsed += grant.optionsLapsed;
    sumOptionsForfeited += grant.optionsForfeited;
    sumTotal =
      sumOptionsVested +
      sumOptionsUnvested +
      sumOptionsNetvested +
      sumOptionsStockExercised +
      sumOptionsCashedOut +
      sumOptionsExercised +
      sumOptionsLapsed +
      sumOptionsForfeited;
  });

  const getWidth = (currentValue: number) => {
    const screen = window.screen;
    const width = screen.width;
    if (sumTotal > 0) return `${(width / sumTotal) * currentValue + 10}%`;
    else return `100%`;
  };

  function handleExportAction(action: Action) {
    if (action.disabled) return;
    if (action.name === "Export All To Excel") {
      exportClickHandler();
    } else if (action.name === "Export Selected") {
      const selectedRows = grantsTableRef.current
        ?.getSelectedRows()
        .map((row) => row.grantInfo.props.grant.optionHolderId);
      const rowsTobeExported = data.filter((dataItem) =>
        selectedRows?.includes(dataItem.optionHolderId)
      );
      exportClickHandler(rowsTobeExported);
    } else if (action.name === "Export Filtered") {
      if (grantsTableRef.current?.isAnyFilterPresent()) {
        const filteredRowIds: string[] = [];
        grantsTableRef.current?.forEachNodeAfterFilter((node: any) => {
          filteredRowIds.push(
            node?.data?.grantInfo?.props?.grant?.optionHolderId
          );
        });
        const filteredRows = data.filter((dataItem) =>
          filteredRowIds.includes(dataItem.optionHolderId)
        );
        exportClickHandler(filteredRows);
      }
    } else if (action.name === "Import From Excel") {
      navigate("/options/onboarding/grants");
    } else if (action.name === "Import Grant letters") {
      navigate("/options/onboarding/uploadGrantLetters");
    } else if (action.name === "Download Template") {
      getGrantTemplate().then((uploadTemplate) => {
        const template = uploadTemplate.data.uploadTemplate;
        convertBase64ToBlob(
          template,
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        ).then((blobObject) => {
          downloadBlobObject(
            blobObject,
            `${companyData?.name} Grant Upload Template.xlsx`
          );
        });
      });
    } else if (action.name === "Download Financial Report") {
      setReportDialog({ open: true, type: "Financial Report" });
    } else if (action.name === "Download Transaction Report") {
      generateTransactionReport().then((data) => {
        const reportDto = data.data;
        convertBase64ToBlob(reportDto.base64File, reportDto.fileType).then(
          (blobObject) => {
            downloadBlobObject(
              blobObject,
              `${companyData?.name} Transaction Report.xlsx`
            );
          }
        );
      });
    } else if (action.name === "Download SH-6 Report") {
      setReportDialog({ open: true, type: "SH-6 Report" });
    } else if (action.name === "Historic Onboarding") {
      navigate("/options/onboarding/historic");
    } else if (action.name === "Download ESOP-Accounting") {
      setReportDialog({ open: true, type: "Esop-Accounting" });
    } else if (action.name === "Download Exercise Report") {
      setReportDialog({ open: true, type: "Exercise Report" });
    }
  }

  const { mutate: exportExcel } = useExportToExcel();
  const { companyData } = useCompanyStore();
  const fileName = `Grant_details_${companyData?.name}`;
  const esopAccountingFileName = `ESOP_Accounting_${getCompanyName()}`;
  const exportClickHandler = (dataToBeExported = data) => {
    const reqData = dataToBeExported?.map((item: any) => {
      item.grantDate = formatDisplayDate(
        new Date(item.grantDate).toUTCString()
      );
      item.optionHolderState = item.optionHolderState.replace(/_/g, " ");
      item.valueAtGrant = item.sharePrice;
      item.equivalentSharesGranted =
        (item.optionsGranted || 0) * (item.ratio || 1);
      item.tags = Array.isArray(item.tags)
        ? item.tags.map((t: any) => t.label).join(", ")
        : "";
      return item;
    });
    setExportData({
      data: reqData,
      headers: getHeaderObj(grantHeaderslist),
      heading: grantHeadingVal,
      open: true,
      fileName,
      tableName: "Grant",
    });
  };
  const getHeaderObj = (keys: string[]) =>
    keys.map((ele: string) => ({
      key: ele,
      value:
        ele === "grantPrice"
          ? `${grantHeadersObj[ele]} (${currencySymbol})`
          : grantHeadersObj[ele],
    }));

  const { mutate: addGrantExcel } = useAddGrantExcel();

  function handleChange(e: any) {
    const file = e.target.files[0];
    const allowedFileExtensions = ["xlsx", "xls", "ods"];
    if (
      allowedFileExtensions.some((extension) => file.name.endsWith(extension))
    ) {
      convertToBase64(file).then((data) => {
        const base64 = {
          file: data as string,
        };
        addGrantExcel(base64, {
          onSuccess: (data) => {
            toast(`Grants uploaded successfully`, {
              type: "success",
            });
          },
        });
      });
    } else {
      toast(
        ` Invalid file type, allowed file types are ${allowedFileExtensions.join(
          ", "
        )}`,
        { type: "error" }
      );
    }
    fileRef.current.value = "";
  }

  const validateUserPermissionForGrant = (
    grant: Grant,
    action: "EDIT" | "STATE_CHANGE"
  ) => true;

  function handleAction(grant: Grant, action: Action) {
    if (action.name === "Edit Grant" && !action.disabled) {
      grantStore.clear();
      navigate(`/options/grant/edit/${grant.optionHolderId}`);
    } else if (action.name === "Clone Grant" && !action.disabled) {
      navigate(`/options/grant/clone/${grant.optionHolderId}`);
    } else if (action.name === "Manage Grant Docs" && !action.disabled) {
      setGrantDocumentDialog({ open: true, grant });
    } else if (action.name === "Update grant status" && !action.disabled) {
      setGrantCreatedDialog({
        open: true,
        grantId: grant.optionHolderId,
        variant: "state change",
      });
    } else if (action.name === "Accelerate Vesting" && !action.disabled) {
      setAccelerateVestingDialog({ open: true, grant });
    } else if (action.name === "Exercise Options" && !action.disabled) {
      setExerciseRequestDialog({ open: true, grant });
    } else if (action.name === "Extend Exercise" && !action.disabled) {
      setExtendExerciseDialog({
        open: true,
        grantIds: [grant.optionHolderId],
      });
    } else if (action.name === "Delete" && !action.disabled) {
      setDialog({
        open: true,
        mode: "Delete",
        grant,
        message: "Do you want to delete this grant?",
      });
    }
  }

  const nonDeletableGrantState = ["GRANTED", "OFFERED", "RESIGNED"];
  const validBulkActions = [
    {
      name: "Delete",
      disabled:
        isBulkDeleteAllowed(selectedItems) ||
        !determineUserAccessToResource(
          permission?.aclList || [],
          Resource.Grant,
          DefaultAction.Delete
        ),
    },
    {
      name: "State Change",
      disabled:
        !isBulkStateChangeAllowed(selectedItems) ||
        !determineUserAccessToResource(
          permission?.aclList || [],
          Resource.Grant,
          DefaultAction.STATE_CHANGE
        ),
    },
    {
      name: "Generate Grant Letter",
      disabled: !(
        selectedItems.length > 0 &&
        determineUserAccessToResource(
          permission?.aclList || [],
          Resource.Grant,
          DefaultAction.Edit
        )
      ),
    },
    {
      name: "Assign Grant Templates",
      disabled: !(
        selectedItems.length > 0 &&
        determineUserAccessToResource(
          permission?.aclList || [],
          Resource.Grant,
          DefaultAction.Edit
        )
      ),
    },

    {
      name: "Download Grant Letters",
      disabled: !(
        selectedItems.length > 0 &&
        determineUserAccessToResource(
          permission?.aclList || [],
          Resource.Grant,
          DefaultAction.Read
        )
      ),
    },
    {
      name: "Create Transactions",
      disabled: !determineUserAccessToResource(
        permission?.aclList || [],
        Resource.Grant,
        DefaultAction.All
      ),
    },
    {
      name: "Grant Report",
      disabled: !(
        selectedItems.length > 0 &&
        determineUserAccessToResource(
          permission?.aclList || [],
          Resource.Grant,
          DefaultAction.Read
        )
      ),
    },
    {
      name: "Vest Wise Report",
      disabled: !(
        selectedItems.length > 0 &&
        determineUserAccessToResource(
          permission?.aclList || [],
          Resource.Grant,
          DefaultAction.Read
        )
      ),
    },
    {
      name: "Exercise Report",
      disabled: !(
        selectedItems.length > 0 &&
        determineUserAccessToResource(
          permission?.aclList || [],
          Resource.Grant,
          DefaultAction.Read
        )
      ),
    },
    {
      name: "Acceptance Report",
      disabled: !(
        selectedItems.length > 0 &&
        determineUserAccessToResource(
          permission?.aclList || [],
          Resource.Grant,
          DefaultAction.Read
        )
      ),
    },
    {
      name: "Cancellation Report",
      disabled: !(
        selectedItems.length > 0 &&
        determineUserAccessToResource(
          permission?.aclList || [],
          Resource.Grant,
          DefaultAction.Read
        )
      ),
    },
    {
      name: "Transaction Report",
      disabled: !(
        selectedItems.length > 0 &&
        determineUserAccessToResource(
          permission?.aclList || [],
          Resource.Grant,
          DefaultAction.Read
        )
      ),
    },
    {
      name: "Send Reminder Emails",
      disabled:
        !isBulkStateChangeAllowedForReminderEmails(selectedItems) ||
        !determineUserAccessToResource(
          permission?.aclList || [],
          Resource.Grant,
          DefaultAction.All
        ),
    },
    ...(empDetailPath?.pathname.includes("employeeDetails")
      ? [
          {
            name: "Extend Exercise",
            disabled:
              selectedItems.length === 0 ||
              selectedItems.reduce(
                (acc, grant) => (grant.netVested || 0) + acc,
                0
              ) <= 0 ||
              !determineUserAccessToResource(
                permission?.aclList || [],
                Resource.Grant,
                DefaultAction.All
              ),
          },
        ]
      : []),
  ];

  function isBulkDeleteAllowed(grants: Grant[]) {
    return grants?.length > 0
      ? grants?.some((grant) =>
          nonDeletableGrantState.includes(grant?.optionHolderState)
        )
      : true;
  }

  function isBulkStateChangeAllowed(grants: Grant[]) {
    const uniqueGrantsByState = _.uniqBy(
      grants,
      (grant) => grant?.optionHolderState
    );
    return (
      uniqueGrantsByState.length === 1 &&
      grants[0].optionHolderState !== "RESIGNED" &&
      grants[0].isStateChangeAllowed &&
      !grants.some((g) => g.isWithdrawLapsed)
    );
  }
  function isBulkStateChangeAllowedForReminderEmails(grants: Grant[]) {
    const uniqueGrantsByState = _.uniqBy(
      grants,
      (grant) => grant?.optionHolderState
    );
    return (
      uniqueGrantsByState.length === 1 &&
      grants[0].optionHolderState === "OFFERED" &&
      grants[0].isStateChangeAllowed &&
      !grants.some((g) => g.isWithdrawLapsed)
    );
  }

  const { mutate: sendReminder, isLoading: sendingReminderEmails } =
    useEmployeeAcceptReminder();

  function bulkSendReminderEmails(grants: Grant[]) {
    const ids = grants.map((grant) => grant.optionHolderId);
    sendReminder(ids, {
      onSuccess: (data) => {
        if ((data?.emailsTriggered || 0) > 0) {
          toast("Reminder Emails triggered successfully..", {
            type: "success",
            autoClose: 2000,
          });
          setBulkActionDialog({ open: false, grantItem: grants });
        } else {
          toast("Error sending emails.. Please check the email settings", {
            type: "error",
            autoClose: 2000,
          });
        }
      },
      onError: (err: any) => {
        const error =
          err.response.data.reason ||
          err.response?.data?.errorRaw ||
          "Something went wrong";
        errorMessage.setMessage(error);
        toast(error, { type: "error", autoClose: 2000 });
      },
    });
  }
  const showToastForReportGeneration = (success: boolean) => {
    if (success) {
      toast(
        `Your report is being generated, please check the reports section to download once it's ready.`,
        {
          type: "success",
          autoClose: 5000,
        }
      );
    } else {
      toast(`Error generating report. Please try again.`, {
        type: "error",
        autoClose: 5000,
      });
    }
  };

  function handleBulkAction(
    action: Action,
    grantItem: Grant[],
    maskPiiData = false
  ): void {
    if (action.disabled) {
      return;
    }
    if (action.name === "State Change") {
      setBulkActionDialog({ open: true, grantItem, mode: "State Change" });
    } else if (action.name === "Delete") {
      setBulkActionDialog({
        open: true,
        grantItem,
        mode: "Delete",
        message: "Do you want to delete these grants?",
      });
    } else if (action.name === "Generate Grant Letter") {
      setBulkActionDialog({
        open: true,
        grantItem,
        mode: "Generate",
        message:
          "Do you want to generate grant letters for these grants? This will replace current grant letters unless uploaded manually.",
      });
    } else if (
      action.name === "Assign Grant Templates" ||
      action.name?.toLowerCase().includes("assign") ||
      action.name?.toLowerCase().includes("template") ||
      action.name?.toLowerCase().includes("upload")
    ) {
      setBulkActionDialog({
        open: true,
        grantItem,
        mode: "Assign Grant Templates",
      });
    } else if (action.name === "Download Grant Letters") {
      if (grantItem.length > 50) {
        toast("Selected data range is too large", {
          type: "error",
          autoClose: 2000,
        });
        return;
      }
      downloadGrantLetters(grantItem.map((grant) => grant.optionHolderId)).then(
        (data) => {
          convertBase64ToBlob(data, "application/zip").then((blobObject) => {
            downloadBlobObject(blobObject, `Grant letters.zip`);
          });
        }
      );
    } else if (action.name === "Grant Report") {
      const grantIds = grantItem.map((grant) => grant.optionHolderId);
      const grantReportDto: ReportType = { grantIds, maskPiiData };
      const fileName = `${"Grant Report"}_${companyName}.xlsx`;
      generateGrantReport(grantReportDto)
        .then((data) => {
          showToastForReportGeneration(true);
        })
        .catch((err: any) => {
          showToastForReportGeneration(false);
        });
    } else if (action.name === "Vest Wise Report") {
      const grantIds = grantItem.map((grant) => grant.optionHolderId);
      const grantReportDto: ReportType = { grantIds, maskPiiData };
      const fileName = `${"Vest Wise Report"}_${companyName}.xlsx`;
      generateVestWiseReport(grantReportDto)
        .then((data) => {
          showToastForReportGeneration(true);
        })
        .catch((err: any) => {
          showToastForReportGeneration(false);
        });
    } else if (action.name === "Exercise Report") {
      const grantIds = grantItem.map((grant) => grant.optionHolderId);
      const grantReportDto: ReportType = { grantIds, maskPiiData };
      const fileName = `${"Exercise Report"}_${companyName}.xlsx`;
      generateExerciseReport(grantReportDto)
        .then((data) => {
          showToastForReportGeneration(true);
        })
        .catch((err: any) => {
          showToastForReportGeneration(false);
        });
    } else if (action.name === "Acceptance Report") {
      const grantIds = grantItem.map((grant) => grant.optionHolderId);
      const grantReportDto: ReportType = { grantIds, maskPiiData };
      const fileName = `${"Acceptance Report"}_${companyName}.xlsx`;
      generateAcceptanceReport(grantReportDto)
        .then((data) => {
          showToastForReportGeneration(true);
        })
        .catch((err: any) => {
          showToastForReportGeneration(false);
        });
    } else if (action.name === "Cancellation Report") {
      const grantIds = grantItem.map((grant) => grant.optionHolderId);
      const grantReportDto: ReportType = { grantIds, maskPiiData };
      const fileName = `${"Cancellation Report"}_${companyName}.xlsx`;
      generateCancellationReport(grantReportDto)
        .then((data) => {
          showToastForReportGeneration(true);
        })
        .catch((err: any) => {
          showToastForReportGeneration(false);
        });
    } else if (action.name === "Transaction Report") {
      const grantIds = grantItem.map((grant) => grant.optionHolderId);
      const grantReportDto: ReportType = { grantIds, maskPiiData };
      const fileName = `${"Transaction Report"}_${companyName}.xlsx`;
      generateTransactionReport(grantReportDto)
        .then((data) => {
          convertBase64ToBlob(
            data?.data?.base64File,
            data?.data?.fileType
          ).then((blobObject) => {
            downloadBlobObject(blobObject, fileName);
          });
        })
        .catch((err: any) => {
          toast(
            err?.response?.data?.errors || "Error while downloading the report",
            { type: "error", autoClose: 2000 }
          );
        });
    } else if (action.name === "Send Reminder Emails") {
      setBulkActionDialog({
        open: true,
        grantItem,
        mode: "Send Reminder Emails",
        message: `Are you sure you want to send Reminder Emails for ${grantItem.length} offer(s)?`,
      });
    } else if (action.name === "Create Transactions") {
      if (action.disabled) {
        return;
      }
      navigate("/options/grants/createGrantTransactions");
    } else if (action.name === "Extend Exercise") {
      const grantIds = grantItem.map((grant) => grant.optionHolderId);
      setExtendExerciseDialog({ open: true, grantIds });
    }
  }
  function getEmployeeDesignation({ grant }: { grant: Grant }) {
    return (employees || []).find(
      (employee) => employee.id.toString() === grant.employeeId.toString()
    )?.employeeDesignation;
  }

  function bulkDeleteOptionGrant(grants: Grant[]) {
    const ids = grants.map((grant) => grant.optionHolderId);
    deleteGrant(ids, {
      onSuccess: () => {
        toast("Grants deleted successfully!", {
          type: "success",
          autoClose: 2000,
        });
        setBulkActionDialog({
          open: false,
          grantItem: bulkActionDialog.grantItem,
        });
      },
      onError: (err: any) => {
        errorMessage.setMessage(err.response.data.reason);
        toast(err.response.data.reason, { type: "error", autoClose: 2000 });
        setBulkActionDialog({
          open: false,
          grantItem: bulkActionDialog.grantItem,
        });
      },
    });
  }

  function bulkGenerateGrantLetters(grants: Grant[]) {
    const ids = grants.map((grant) => grant.optionHolderId);
    generateGrantLetters(ids, {
      onSuccess: () => {
        toast(
          "Grant letters generation initiated successfully. Please check the progress bar",
          {
            type: "success",
            autoClose: 500,
          }
        );
        setBulkActionDialog({
          open: false,
          grantItem: bulkActionDialog.grantItem,
        });
      },
      onError: (err: any) => {
        errorMessage.setMessage(err.response.data.reason);
        toast(err.response.data.reason, { type: "error", autoClose: 2000 });
        setBulkActionDialog({
          open: false,
          grantItem: bulkActionDialog.grantItem,
        });
      },
    });
  }

  function deleteOptionGrant() {
    const optionHolderId = dialog.grant?.optionHolderId || "";
    deleteGrant([optionHolderId], {
      onSuccess: () => {
        toast("Grant deleted successfully!", {
          type: "success",
          autoClose: 2000,
        });
        setDialog({ open: false });
      },
      onError: (err: any) => {
        errorMessage.setMessage(err.response.data.reason);
        toast(err.response.data.reason, { type: "error", autoClose: 2000 });
        setDialog({ open: false });
      },
    });
  }
  function handleActions() {
    grantFilter.setApplyFilter(!grantFilter.applyFilter);
  }
  function reset(
    minOptionsVested: number,
    maxOptionsVested: number,
    minOptionsGranted: number,
    maxOptionsGranted: number
  ) {
    grantFilter.reset(
      minOptionsVested,
      maxOptionsVested,
      minOptionsGranted,
      maxOptionsGranted
    );
  }
  function viewEmployee(grant: Grant) {
    navigate(`/options/employeeDetails/${grant.employeeId}`);
  }

  const initialColumnState = [
    {
      name: "holders-name",
      isVisible: true,
      isDisabled: false,
    },
    {
      name: "grant-info",
      isVisible: true,
      isDisabled: false,
    },
    {
      name: "options-granted",
      isVisible: true,
      isDisabled: false,
    },
    {
      name: "vested",
      isVisible: true,
      isDisabled: false,
    },
    {
      name: "unvested",
      isVisible: true,
      isDisabled: false,
    },
    {
      name: "net-vested",
      isVisible: true,
      isDisabled: false,
    },
    {
      name: "stock-exercised",
      isVisible: true,
      isDisabled: false,
    },
    {
      name: "cashed-out",
      isVisible: true,
      isDisabled: false,
    },
    {
      name: "total-exercised",
      isVisible: true,
      isDisabled: false,
    },
    {
      name: "forfeited",
      isVisible: true,
      isDisabled: false,
    },
  ];
  const [columnOptions, setColumnOptions] =
    useState<ColumnOptions[]>(initialColumnState);
  const { isMobile } = useIsMobile();
  function handleClose(): void {
    throw new Error("Function not implemented.");
  }

  function handleSelect(): void {
    throw new Error("Function not implemented.");
  }

  function handleTableFilterCount(isData?: boolean) {
    const grantsCount = grantsTableRef?.current?.getModel().getRowCount();
    setGrantsCount(grantsCount ?? 0);
    for (let i = 0; i < grantsCount!; i++) {
      const rowNode = grantsTableRef?.current?.getDisplayedRowAtIndex(i);
      rowData.push(rowNode?.data);
    }

    const uniqueByEmployeeDetails = rowData.reduce((acc: any, current: any) => {
      if (!acc.some((item: any) => item?.employeeId === current?.employeeId)) {
        acc.push(current);
      }
      return acc;
    }, []);

    setUniqueEmployeeDetails(uniqueByEmployeeDetails?.length ?? 0);
  }

  return (
    <Box
      // class=" overflow-x-auto p-4 bg-white rounded-lg "
      className={`${
        isMobile ? "min-w-[1040px] overflow-x-auto" : "overflow-x-auto"
      }  p-4 bg-white rounded-lg border border-borderColor drop-shadow-box  `}
    >
      <HStack aria-label="toolbar" className="justify-between">
        <GenericTableHeader
          heading={"Grant Details"}
          subHeading={""}
          customClassName="flex flex-col px-4 lg:justify-start"
          additionalInfo={
            <>
              {grantsCount} Grants,
              {uniqueByEmployeeDetails} Employees
            </>
          }
          selectionCount={selectedItems.length}
        ></GenericTableHeader>
        {/* <VStack className="min-w-max">
          <p className="text-lg font-medium text-gray-dark">Grant Details</p>
          <p className="text-xs font-medium text-gray-light">
            {data.length} Grants,
            {new Set(data.map((dataItem) => dataItem.employeeId)).size}{" "}
            Employees
          </p>
        </VStack> */}
        <HStack className="h-8 min-w-min">
          <Center>
            <BulkActions
              actions={validBulkActions}
              onAction={(action) => {
                if (action.name.includes("Report")) {
                  setCommonReportDialog({
                    open: true,
                    action,
                    grants: selectedItems,
                  });
                } else {
                  handleBulkAction(action, selectedItems);
                }
              }}
            />
          </Center>
          {!empDetailPath?.pathname.includes("employeeDetails") ? (
            <>
              <Center
                className={`p-2 px-3 mx-2 text-white rounded hidden ${
                  grantFilter.filterButtonFocused
                    ? "bg-orange-501"
                    : "bg-slate-100"
                } `}
              >
                <AdvanceFilter
                  primaryAction={handleActions}
                  secondaryAction={(
                    minOptionsVested,
                    maxOptionsVested,
                    minOptionsGranted,
                    maxOptionsGranted
                  ) => {
                    reset(
                      minOptionsVested,
                      maxOptionsVested,
                      minOptionsGranted,
                      maxOptionsGranted
                    );
                  }}
                  data={_grants}
                />
              </Center>
              <HStack className="hidden min-w-72 bg-slate-light text-slate-dark">
                <Icon icon="fe:search" width="24" className="mr-2 " />
                <input
                  type="text"
                  className="w-full text-xs font-normal border-0 outline-none bg-inherit"
                  placeholder="Search by Name, Plan, Status"
                  value={globalFilterText}
                  onChange={(e) => {
                    setGlobalFilterText(e.target.value);
                  }}
                ></input>
              </HStack>
              <HStack className="items-center justify-between mx-2 rounded bg-slate-light">
                <input
                  ref={fileRef}
                  onChange={handleChange}
                  multiple={false}
                  type="file"
                  accept=".xlsx, .xls, .ods"
                  hidden
                />
                <ExportImport
                  actions={[
                    {
                      name: "Export All To Excel",
                    },
                    {
                      name: "Export Selected",
                      disabled:
                        grantsTableRef.current?.getSelectedRows()?.length === 0,
                    },
                    {
                      name: "Export Filtered",
                      disabled: !grantsTableRef.current?.isAnyFilterPresent(),
                    },
                    {
                      name: "Import From Excel",
                      disabled: !determineUserAccessToResource(
                        permission?.aclList || [],
                        Resource.Grant,
                        DefaultAction.Create
                      ),
                    },
                    {
                      name: "Import Grant letters",
                      disabled: !determineUserAccessToResource(
                        permission?.aclList || [],
                        Resource.Grant,
                        DefaultAction.Create
                      ),
                    },
                    {
                      name: "Download Template",
                    },

                    {
                      name: "Historic Onboarding",
                      disabled: !determineUserAccessToResource(
                        permission?.aclList || [],
                        Resource.All,
                        DefaultAction.All
                      ),
                    },
                  ]}
                  onAction={(action) => handleExportAction(action)}
                />
              </HStack>
            </>
          ) : (
            ""
          )}
          {reportDialog.type === "Financial Report" && (
            <Dialog
              open={reportDialog.open}
              onClose={() => setReportDialog({ open: false })}
              maxWidth="lg"
            >
              <div className="w-[400px] min-h-min mx-auto bg-white rounded-lg">
                <FinancialReportDialog
                  onClose={() => setReportDialog({ open: false })}
                />
              </div>
            </Dialog>
          )}
          {commonReportDialog.open && (
            <AlertDialog
              open={commonReportDialog.open}
              message="Hide all Personally Identifiable Information"
              primaryActionText="Download"
              secondaryActionText="Cancel"
              onPrimaryAction={() => {
                handleBulkAction(
                  commonReportDialog.action,
                  commonReportDialog.grants,
                  maskPiiData
                );
                setCommonReportDialog({
                  open: false,
                  action: { name: "" },
                  grants: [],
                });
              }}
              onSecondaryAction={() => {
                setCommonReportDialog({
                  open: false,
                  action: { name: "" },
                  grants: [],
                });
              }}
              checkBox={maskPiiData}
              setCheckBox={setMaskPiiData}
            />
          )}
          {reportDialog.type === "Exercise Report" && (
            <Dialog
              open={reportDialog.open}
              onClose={() => setReportDialog({ open: false })}
              maxWidth="lg"
            >
              <div className="w-[500px] min-h-min mx-auto bg-white rounded-lg">
                <ExerciseReportDialog
                  onClose={() => setReportDialog({ open: false })}
                />
              </div>
            </Dialog>
          )}
          {reportDialog.type === "SH-6 Report" && (
            <Dialog
              open={reportDialog.open}
              onClose={() => setReportDialog({ open: false })}
              maxWidth="lg"
            >
              <div className="mx-auto bg-white rounded-lg min-w-min min-h-min">
                <SH6ReportDialog
                  onClose={() => setReportDialog({ open: false })}
                />
              </div>
            </Dialog>
          )}
          {reportDialog.type === "Esop-Accounting" && (
            <Dialog
              open={reportDialog.open}
              onClose={() => setReportDialog({ open: false })}
              maxWidth="lg"
            >
              <div className="w-[500px] min-h-min mx-auto bg-white rounded-lg">
                <EsopAccountingDialog
                  onClose={() => setReportDialog({ open: false })}
                />
              </div>
            </Dialog>
          )}
          <ButtonPrimary
            buttonSize={ButtonSize.SMALL}
            className={
              !determineUserAccessToResource(
                permission?.aclList || [],
                Resource.Grant,
                DefaultAction.Create
              )
                ? "opacity-50 cursor-not-allowed min-w-max"
                : "min-w-max"
            }
            disabled={
              !determineUserAccessToResource(
                permission?.aclList || [],
                Resource.Grant,
                DefaultAction.Create
              ) ||
              (empDetailPath?.pathname.includes("employeeDetails")
                ? employeeData[0]?.employmentStatus !==
                  EmployementStatus.EMPLOYED
                : false)
            }
            onClick={() => navigate("/options/grant/add")}
          >
            Create a Grant
          </ButtonPrimary>
          <Dialog
            open={dialog.open}
            onClose={() => setDialog({ open: false })}
            maxWidth="md"
          >
            {dialog.mode === "Delete" ? (
              <AlertDialog
                onClose={() => setDialog({ open: false })}
                open={dialog.open}
                message={dialog.message}
                onPrimaryAction={deleteOptionGrant}
                onSecondaryAction={() => setDialog({ open: false })}
                loading={deletingGrant}
              />
            ) : (
              <AddOrEditGrant
                onClose={() => setDialog({ open: false })}
                grant={dialog.grant || undefined}
                mode={dialog.mode}
              />
            )}
          </Dialog>
          <Dialog
            open={documentDialog.open}
            onClose={() => setDocumentDialog({ open: false })}
            maxWidth="lg"
          >
            <LazyPdfDocument
              url={documentDialog?.grant?.grantLetterLink ?? ""}
              name={`${documentDialog?.grant?.employeeName}-
              ${documentDialog?.grant?.grantIdentifier}.pdf`}
            />
          </Dialog>
          <Dialog
            open={grantDocumentDialog.open}
            onClose={() => setGrantDocumentDialog({ open: false })}
            maxWidth="lg"
          >
            {grantDocumentDialog.grant && (
              <div className="min-w-full lg:w-[1200px]">
                <GrantDocumentDialog
                  grant={grantDocumentDialog.grant}
                  onClose={() => setGrantDocumentDialog({ open: false })}
                />
              </div>
            )}
          </Dialog>
          <Dialog
            open={vestRequestDialog.open}
            onClose={() => setVestRequestDialog({ open: false })}
            maxWidth="md"
          >
            {vestRequestDialog.grant && (
              <div className="w-[900px]">
                <VestRequest
                  grant={vestRequestDialog.grant}
                  onClose={() => setVestRequestDialog({ open: false })}
                />
              </div>
            )}
          </Dialog>
          <Dialog
            open={
              bulkActionDialog.open &&
              bulkActionDialog.mode !== "Assign Grant Templates"
            }
            onClose={() =>
              setBulkActionDialog({
                open: false,
                grantItem: bulkActionDialog.grantItem,
              })
            }
          >
            {bulkActionDialog.mode === "State Change" ? (
              <BulkOptionStateChange
                grants={bulkActionDialog.grantItem || []}
                onClose={() =>
                  setBulkActionDialog({
                    open: false,
                    grantItem: bulkActionDialog.grantItem,
                  })
                }
              />
            ) : bulkActionDialog.mode === "Delete" ? (
              <AlertDialog
                onClose={() =>
                  setBulkActionDialog({
                    open: false,
                    grantItem: bulkActionDialog.grantItem,
                  })
                }
                open={bulkActionDialog.open}
                message={bulkActionDialog.message}
                onPrimaryAction={() =>
                  bulkDeleteOptionGrant(bulkActionDialog.grantItem)
                }
                onSecondaryAction={() =>
                  setBulkActionDialog({
                    open: false,
                    grantItem: bulkActionDialog.grantItem,
                  })
                }
                loading={deletingGrant}
              />
            ) : bulkActionDialog.mode === "Send Reminder Emails" ? (
              <AlertDialog
                onClose={() =>
                  setBulkActionDialog({
                    open: false,
                    grantItem: bulkActionDialog.grantItem,
                  })
                }
                open={bulkActionDialog.open}
                message={bulkActionDialog.message}
                onPrimaryAction={() =>
                  bulkSendReminderEmails(bulkActionDialog.grantItem)
                }
                onSecondaryAction={() =>
                  setBulkActionDialog({
                    open: false,
                    grantItem: bulkActionDialog.grantItem,
                  })
                }
                loading={sendingReminderEmails}
              />
            ) : bulkActionDialog.mode === "Assign Grant Templates" ? (
              <div />
            ) : (
              <AlertDialog
                onClose={() =>
                  setBulkActionDialog({
                    open: false,
                    grantItem: bulkActionDialog.grantItem,
                  })
                }
                open={bulkActionDialog.open}
                message={bulkActionDialog.message}
                onPrimaryAction={() =>
                  bulkGenerateGrantLetters(bulkActionDialog.grantItem)
                }
                onSecondaryAction={() =>
                  setBulkActionDialog({
                    open: false,
                    grantItem: bulkActionDialog.grantItem,
                  })
                }
                loading={generatingGrant}
              />
            )}
          </Dialog>
        </HStack>
      </HStack>
      <Box
        className={` p-4 bg-white rounded-lg  ${
          isMobile ? "min-w-[1040px]" : "w-full"
        }`}
      >
        <HStack className="justify-between w-full ">
          <AgGridGrantsTable
            key={"GrantsTable"}
            grantTableData={data}
            handleAction={handleAction}
            onSelectedRows={onSelectionChange}
            gridRef={grantsTableRef}
            tableFilterCount={handleTableFilterCount}
          />
        </HStack>
      </Box>
      <ExtendExerciseDialog
        open={extendExerciseDialog.open}
        onClose={() =>
          setExtendExerciseDialog({ ...extendExerciseDialog, open: false })
        }
        grantIds={extendExerciseDialog.grantIds}
        onSuccess={() => {
          grantFilter.setApplyFilter(!grantFilter.applyFilter); // Refresh table
        }}
      />
      <Dialog open={exportData?.open || false} maxWidth="md">
        <ExportTableData
          onDiscard={() => setExportData({ ...exportData, open: false })}
          data={exportData?.data || []}
          fileName={exportData?.fileName || ""}
          headers={exportData?.headers || []}
          heading={exportData?.heading || ""}
          tableName={exportData?.tableName || ""}
        />
      </Dialog>
      <AssignGrantLetterTemplatesModal
        open={
          bulkActionDialog.open &&
          bulkActionDialog.mode === "Assign Grant Templates"
        }
        onClose={() =>
          setBulkActionDialog({
            open: false,
            grantItem: bulkActionDialog.grantItem,
          })
        }
        grants={bulkActionDialog.grantItem || []}
      />
    </Box>
  );
}

export default GrantsTable;

export function isVestAllowed(grant: Grant) {
  return (
    grant.optionHolderState === "GRANTED" &&
    getVestableOptions(grant) > 0 &&
    grant.vestingTriggerType !== TriggerType.TIME &&
    !grant.isVestRequested
  );
}

export function isExerciseAllowed(grant: Grant) {
  return grant.isExerciseAllowed;
}

export function isAccelerationAllowed(grant: Grant) {
  return (
    grant.optionHolderState === "GRANTED" &&
    grant.availableToAccelerate > 0 &&
    !grant.isPausedVesting
  );
}

export function getVestableOptions(grant: Grant) {
  return grant.optionsGranted - grant.optionsVested - grant.optionsLapsed;
}

export function BulkActions({
  actions,
  onAction,
  title = "Bulk Actions",
}: BulkActionsProps) {
  return (
    <CTADropdown
      className="items-center"
      actions={actions}
      onAction={onAction}
      render={() => (
        <div className="flex items-center h-8 p-2 px-2 mx-2 rounded text-slate-dark bg-slate-light">
          <span>{title}</span>
          <Icon height={24} icon="ri:arrow-drop-down-line" className="inline" />
        </div>
      )}
    />
  );
}
export function ExportImport({
  actions,
  onAction,
}: {
  actions: Action[];
  onAction: (action: Action) => void;
}) {
  return (
    <CTADropdown
      className="items-center"
      actions={actions}
      onAction={onAction}
      render={() => (
        <div className="flex items-center h-8 p-2 px-2 mx-2 rounded text-slate-dark bg-slate-light">
          <span>Export/Import</span>
          <Icon height={24} icon="ri:arrow-drop-down-line" className="inline" />
        </div>
      )}
    />
  );
}

export function AdvanceFilter({
  primaryAction,
  secondaryAction,
  data,
}: {
  primaryAction: () => void;
  secondaryAction: (
    minOptionsVested: number,
    maxOptionsVested: number,
    minOptionsGranted: number,
    maxOptionsGranted: number
  ) => void;
  data: Grant[];
}) {
  const grantFilter = useGrantFilterStore();
  return (
    <ContextModal
      primaryAction={primaryAction}
      secondaryAction={secondaryAction}
      data={data}
      className="items-end"
      render={() => (
        <Icon
          icon="el:filter"
          className={`${
            !grantFilter.filterButtonFocused ? "text-orange-501" : "text-white"
          }  rounded`}
          height={20}
        />
      )}
    />
  );
}

export function InColumnRangeFilters({
  primaryAction,
  secondaryAction,
  name,
  type,
  data,
}: {
  primaryAction: () => void;
  secondaryAction: () => void;
  name: any;
  type: string;
  data: Grant[];
}) {
  const grantFilter = useGrantFilterStore();
  const lowest = 0;
  const _highest = data.map((item: any) => parseInt(item[name], 10));
  const highest: number = _.max(_highest) || 0;
  return (
    <TableRangeFilters
      primaryAction={primaryAction}
      secondaryAction={secondaryAction}
      lowest={lowest}
      highest={highest}
      className="items-end"
      name={name}
      render={() => (
        <Icon
          icon="material-symbols:filter-list-rounded"
          className={`${
            !grantFilter.filterButtonFocused ? "text-orange-501" : "text-white"
          }  rounded`}
          height={20}
        />
      )}
    />
  );
}

export function InColumnDateFilters({
  primaryAction,
  secondaryAction,
  name,
  type,
  data,
}: {
  primaryAction: () => void;
  secondaryAction: () => void;
  name: any;
  type: string;
  data: Grant[];
}) {
  const grantFilter = useGrantFilterStore();
  return (
    <TableDateFilters
      primaryAction={primaryAction}
      secondaryAction={secondaryAction}
      data={data}
      className="items-end"
      name={name}
      render={() => (
        <Icon
          icon="material-symbols:filter-list-rounded"
          className={`${
            !grantFilter.filterButtonFocused ? "text-orange-501" : "text-white"
          }  rounded`}
          height={20}
        />
      )}
    />
  );
}
